import {
  aigcCreateTaskId,
  aigcGetSteamingSummary,
  aigcGetSummary,
  aigcGetVideoSummary,
} from '@/pages/opinion/services';
import {
  AigcSummaryType,
  GPTResultType,
  GPTErrorStatusType,
  ScenarioType,
  GPTType,
  PromptListType,
  PromptType,
} from '../types';
import { GPTStatus } from '@op/constants/GPT';
import { dealwithScenarioMap } from './dealwithScenario';
import { LocaleType } from '@op/types/common';
import { getLocale, useIntl, useSelector } from 'umi';
import { DEFAULT_MODAL } from '../constants';

let cancel: (() => void) | null = null;
export async function getAdvanceSummary({
  filter,
  stop,
  promptInfo,
  cancelCallback,
  oriFilter,
  savePrefix,
  shortIdErrorMessage = 'The network is unstable. Failed to load filters. Please try again.',
}: AigcSummaryType) {
  // 多次调用，先取消了
  cancel?.();
  cancel = null;
  function getCancelInfo(tips: GPTErrorStatusType): GPTResultType {
    if (cancelCallback?.()) {
      return { status: 'cancel', data: '', tips: 'GPT_cancel' };
    }
    return { status: 'failed', data: '', tips };
  }
  function sleep() {
    return new Promise((resolve, _reject) => {
      setTimeout(() => {
        resolve(cancelCallback?.());
      }, 5000);
    });
  }

  async function getGptResult(
    taskId: string,
  ): Promise<GPTResultType | undefined> {
    try {
      const data = await aigcGetSummary(
        { game_id: filter.edition_unified_id, task_id: taskId },
        (cancelToken: any) => {
          if (stop) stop.current = cancelToken;
          else cancel = cancelToken;
        },
      ).catch((error: any) => {
        return getCancelInfo('GPT_failed');
      });
      // 将GPT状态收缩到GPTStatus中的四个状态中：Done | Failed | No data | Generating
      const status = data.task_status;
      const { text } = GPTStatus[status] || { text: 'In queue' };

      if (text === 'Done') {
        return { data, status: 'success' };
      }
      if (['Summary failed', 'No data'].includes(text)) {
        return getCancelInfo(`GPT_${data.task_status}` as 'GPT_failed');
      }
      if (!cancelCallback?.()) {
        const flag = await sleep();
        if (!flag) {
          return await getGptResult(taskId);
        }
        return getCancelInfo('GPT_failed');
      }
    } catch (error: any) {
      if (error?.code !== 999 && error?.code !== '999') {
        await sleep();
        const flag = await sleep();
        if (!flag) {
          return await getGptResult(taskId);
        }
      } else {
        return getCancelInfo('GPT_failed');
      }
    }
  }

  let taskId: string | null | undefined = filter.task_id;
  // 如果没有taskId，那么就说明要创建一个taskId
  if (!taskId) {
    try {
      const createResult = await aigcCreateTaskId({
        filter,
        promptInfo,
        oriFilter,
        cancelCallback: (cancelToken: any) => {
          if (stop) stop.current = cancelToken;
          else cancel = cancelToken;
        },
        savePrefix,
        shortIdErrorMessage,
      });
      taskId = createResult?.task_id;
    } catch (error: any) {
      if (error.code === 30018) {
        return getCancelInfo('GPT_maxLimit');
      }
      return getCancelInfo('GPT_failed');
    }
  }

  // 如果taskId为空，那么就说明创建失败了
  if (!taskId) return getCancelInfo('GPT_failed');

  const result = await getGptResult(taskId);
  if (!result) return result;

  if (typeof result.data === 'string') return result;

  const key = promptInfo?.scenario as ScenarioType;
  const dealwithFunc = dealwithScenarioMap[key] || dealwithScenarioMap.default;
  return { ...result, data: dealwithFunc(result.data) };
}

export function useAdvanceSummary() {
  const { formatMessage } = useIntl();
  const { oriFilter } = useSelector(
    (state: any) => state.opinionAIShortIdFilter,
  );

  function request(props: AigcSummaryType) {
    return getAdvanceSummary({
      ...props,
      oriFilter,
      shortIdErrorMessage: formatMessage({
        id: 'The network is unstable. Failed to load filters. Please try again.',
      }),
    });
  }

  return {
    getAdvanceSummaryHooks: request,
  };
}

export async function getVideoFeedsSummary({
  filter,
  stop,
  promptInfo,
  cancelCallback,
}: AigcSummaryType) {
  // 多次调用，先取消了
  cancel?.();
  cancel = null;

  function getCancelInfo(tips: GPTErrorStatusType): GPTResultType {
    if (cancelCallback?.()) {
      return { status: 'cancel', data: '', tips: 'GPT_cancel' };
    }
    return { status: 'failed', data: '', tips };
  }
  function sleep() {
    return new Promise((resolve, _reject) => {
      setTimeout(() => {
        resolve(cancelCallback?.());
      }, 60 * 1000);
    });
  }

  async function getGptResult(): Promise<GPTResultType | undefined> {
    try {
      const id = filter?.comment_uins?.[0];
      const data = await aigcGetVideoSummary(filter, (cancelToken: any) => {
        if (stop) stop.current = cancelToken;
        else cancel = cancelToken;
      });

      const newData = data?.[id];
      if (!newData) {
        return getCancelInfo('GPT_failed');
      }

      // 30004: 系统错误 / 40018: 无数据
      const { error_code, status, summary_cn, summary_en, sentiment } = newData;

      if (status === 'success') {
        if (!summary_cn && !summary_en) {
          return getCancelInfo('GPT_nodataSuccess');
        }
        return {
          data: {
            report_en: summary_en,
            report_zh: summary_cn,
            sentiment,
            task_status: status,
          },
          status: 'success',
        };
      }
      if (error_code === '40001') {
        return getCancelInfo('GPT_nodata');
      }
      if (error_code !== '0') {
        return getCancelInfo('GPT_failed');
      }
      if (!cancelCallback?.()) {
        const flag = await sleep();
        if (!flag) {
          return await getGptResult();
        }
      }
    } catch (error: any) {
      if (error?.code !== '999') {
        await sleep();
        const flag = await sleep();
        if (!flag) {
          return await getGptResult();
        }
      }
    }
  }

  const result = await getGptResult();
  if (!result) return result;

  if (typeof result.data === 'string') return result;

  const key = promptInfo.scenario as ScenarioType;
  const dealwithFunc = dealwithScenarioMap[key] || dealwithScenarioMap.default;
  return { ...result, data: dealwithFunc(result.data) };
}

export async function getSteamingFeedsSummary({
  filter,
  stop,
  cancelCallback,
}: Omit<AigcSummaryType, 'promptInfo'>) {
  // 多次调用，先取消了
  cancel?.();
  cancel = null;

  function getCancelInfo(tips: GPTErrorStatusType): GPTResultType {
    if (cancelCallback?.()) {
      return { status: 'cancel', data: '', tips: 'GPT_cancel' };
    }
    return { status: 'failed', data: '', tips };
  }
  function sleep() {
    return new Promise((resolve, _reject) => {
      setTimeout(() => {
        resolve(cancelCallback?.());
      }, 6 * 1000);
    });
  }

  async function getGptResult(): Promise<GPTResultType | undefined> {
    try {
      const data = await aigcGetSteamingSummary(filter, (cancelToken: any) => {
        if (stop) stop.current = cancelToken;
        else cancel = cancelToken;
      });

      if (!data) {
        return { status: 'cancel', data: '' };
      }

      // 30004: 系统错误 / 40018: 无数据
      const { error_code, status, summary_cn, summary_en } = data;

      if (status === 'success') {
        if (!summary_cn && !summary_en) {
          return getCancelInfo('GPT_nodataSuccess');
        }
        return {
          data: {
            report_en: summary_en,
            report_zh: summary_cn,
            task_status: status,
          },
          status: 'success',
        };
      }
      if (error_code === '40001') {
        return getCancelInfo('GPT_nodata');
      }
      if (error_code !== '0') {
        return getCancelInfo('GPT_failed');
      }
      if (!cancelCallback?.()) {
        const flag = await sleep();
        if (!flag) {
          return await getGptResult();
        }
      }
    } catch (error: any) {
      if (error?.code !== '999') {
        await sleep();
        const flag = await sleep();
        if (!flag) {
          return await getGptResult();
        }
      }
    }
  }

  const result = await getGptResult();
  if (!result) return result;

  if (typeof result.data === 'string') return result;

  const dealwithFunc = dealwithScenarioMap.default;
  return { ...result, data: dealwithFunc(result.data) };
}

export function getPromptText(item?: PromptType) {
  const lang: LocaleType = getLocale();
  return item?.[lang] || '';
}

/**
 * 获取默认值
 * @param active
 * @returns
 */
export function getDefaultPromptMap(
  promptList: PromptListType,
  active = 1,
  type: GPTType = DEFAULT_MODAL,
) {
  function getArrayPromptText(list: Array<PromptType>) {
    const item = list?.find((item) => item.value === active);
    return item;
  }
  if (Array.isArray(promptList)) {
    return getArrayPromptText(promptList);
  }

  return getArrayPromptText(promptList?.[type]);
}

/**
 * 获取默认值
 * @param active
 * @returns
 */
export function getDefaultPromptText(
  promptList: PromptListType,
  active = 1,
  type: GPTType = DEFAULT_MODAL,
) {
  const item = getDefaultPromptMap(promptList, active, type);

  return getPromptText(item);
}

const aiEmailListStoreKey = 'dbo_ai_email_list';

function getSaveEmailKey(userInfo: any) {
  return `${userInfo.openid}_${aiEmailListStoreKey}`;
}

export function getAiSaveEmailList(userInfo: any) {
  try {
    if (userInfo) {
      const key = getSaveEmailKey(userInfo);
      const list = localStorage.getItem(key);
      if (list) {
        return JSON.parse(list);
      }
    }
  } catch (error) {}
  return [];
}

export function saveAiEmailList(userInfo: any, data: Array<string>) {
  try {
    if (userInfo) {
      const key = getSaveEmailKey(userInfo);
      localStorage.setItem(key, JSON.stringify(data));
    }
  } catch (error) {}
}
